import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    // Pub Package imports
    getAllPubPackages,
    getPubPackageById,
    getFeaturedPubPackages,
    createPubPackage,
    updatePubPackage,
    deletePubPackage,
    movePubPackageUp,
    movePubPackageDown,
    PubPackage
} from '../firebase/pubPackageService';
import {
    getAllStarterTemplates as getTemplates,
    getStarterTemplateById as getTemplateById,
    getFeaturedStarterTemplates as getFeaturedTemplates,
    getStarterTemplatesByCategory as getTemplatesByCategory,
    getStarterTemplatesByType as getTemplatesByType,
    createStarterTemplate as createTemplate,
    updateStarterTemplate as updateTemplate,
    deleteStarterTemplate as deleteTemplate,
    moveStarterTemplateUp as moveTemplateUp,
    moveStarterTemplateDown as moveTemplateDown,
    StarterTemplate as Template
} from '../firebase/starterTemplateService';
import {
    getAllBlogs as getBlogs,
    getBlogById as getBlog,
    getBlogBySlug,
    getFeaturedBlogs as getFeaturedBlogPosts,
    searchBlogs as searchBlogPosts,
    getRelatedBlogs as getRelatedBlogPosts,
    createBlog as createBlogPost,
    updateBlog as updateBlogPost,
    deleteBlog as deleteBlogPost,
    incrementBlogViews,
    getAllBlogCategories as getBlogCategories,
    createBlogCategory as createCategory,
    updateBlogCategory as updateCategory,
    deleteBlogCategory as deleteCategory,
    Blog as BlogPost,
    BlogCategory
} from '../firebase/blogService';

// ============================================================================
// PUB PACKAGES QUERY KEYS
// ============================================================================
export const pubPackageKeys = {
    all: ['pubPackages'] as const,
    lists: () => [...pubPackageKeys.all, 'list'] as const,
    list: (filters: string) => [...pubPackageKeys.lists(), { filters }] as const,
    featured: () => [...pubPackageKeys.all, 'featured'] as const,
    details: () => [...pubPackageKeys.all, 'detail'] as const,
    detail: (id: string) => [...pubPackageKeys.details(), id] as const,
};

// ============================================================================
// PUB PACKAGES HOOKS
// ============================================================================

/**
 * Get all pub packages
 */
export const usePubPackagesQuery = () => {
    return useQuery({
        queryKey: pubPackageKeys.lists(),
        queryFn: getAllPubPackages,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Get featured pub packages
 */
export const useFeaturedPubPackagesQuery = () => {
    return useQuery({
        queryKey: pubPackageKeys.featured(),
        queryFn: getFeaturedPubPackages,
        staleTime: 10 * 60 * 1000, // 10 minutes
    });
};

/**
 * Get pub package by ID
 */
export const usePubPackageQuery = (id: string) => {
    return useQuery({
        queryKey: pubPackageKeys.detail(id),
        queryFn: () => getPubPackageById(id),
        enabled: !!id,
        staleTime: 5 * 60 * 1000,
    });
};

/**
 * Create pub package mutation
 */
export const useCreatePubPackageMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createPubPackage,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: pubPackageKeys.all });
        },
    });
};

/**
 * Update pub package mutation
 */
export const useUpdatePubPackageMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<Omit<PubPackage, 'id' | 'createdAt' | 'updatedAt'>> }) =>
            updatePubPackage(id, data),
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: pubPackageKeys.all });
            queryClient.invalidateQueries({ queryKey: pubPackageKeys.detail(id) });
        },
    });
};

/**
 * Delete pub package mutation
 */
export const useDeletePubPackageMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deletePubPackage,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: pubPackageKeys.all });
        },
    });
};

// ============================================================================
// STARTER TEMPLATES QUERY KEYS
// ============================================================================
export const starterTemplateKeys = {
    all: ['starterTemplates'] as const,
    lists: () => [...starterTemplateKeys.all, 'list'] as const,
    list: (filters: string) => [...starterTemplateKeys.lists(), { filters }] as const,
    featured: () => [...starterTemplateKeys.all, 'featured'] as const,
    categories: () => [...starterTemplateKeys.all, 'categories'] as const,
    category: (category: string) => [...starterTemplateKeys.categories(), category] as const,
    types: () => [...starterTemplateKeys.all, 'types'] as const,
    type: (type: string) => [...starterTemplateKeys.types(), type] as const,
    details: () => [...starterTemplateKeys.all, 'detail'] as const,
    detail: (id: string) => [...starterTemplateKeys.details(), id] as const,
};

// ============================================================================
// STARTER TEMPLATES HOOKS
// ============================================================================

/**
 * Get all starter templates
 */
export const useStarterTemplatesQuery = () => {
    return useQuery({
        queryKey: starterTemplateKeys.lists(),
        queryFn: getTemplates,
        staleTime: 5 * 60 * 1000,
    });
};

/**
 * Get featured starter templates
 */
export const useFeaturedStarterTemplatesQuery = () => {
    return useQuery({
        queryKey: starterTemplateKeys.featured(),
        queryFn: getFeaturedTemplates,
        staleTime: 10 * 60 * 1000,
    });
};

/**
 * Get starter templates by category
 */
export const useStarterTemplatesByCategoryQuery = (category: string) => {
    return useQuery({
        queryKey: starterTemplateKeys.category(category),
        queryFn: () => getTemplatesByCategory(category),
        enabled: !!category,
        staleTime: 5 * 60 * 1000,
    });
};

/**
 * Get starter templates by type
 */
export const useStarterTemplatesByTypeQuery = (type: 'template' | 'extension' | 'tool' | 'boilerplate') => {
    return useQuery({
        queryKey: starterTemplateKeys.type(type),
        queryFn: () => getTemplatesByType(type),
        enabled: !!type,
        staleTime: 5 * 60 * 1000,
    });
};

/**
 * Get starter template by ID
 */
export const useStarterTemplateQuery = (id: string) => {
    return useQuery({
        queryKey: starterTemplateKeys.detail(id),
        queryFn: () => getTemplateById(id),
        enabled: !!id,
        staleTime: 5 * 60 * 1000,
    });
};

/**
 * Create starter template mutation
 */
export const useCreateStarterTemplateMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createTemplate,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: starterTemplateKeys.all });
        },
    });
};

/**
 * Update starter template mutation
 */
export const useUpdateStarterTemplateMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<Omit<Template, 'id' | 'createdAt' | 'updatedAt'>> }) =>
            updateTemplate(id, data),
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: starterTemplateKeys.all });
            queryClient.invalidateQueries({ queryKey: starterTemplateKeys.detail(id) });
        },
    });
};

/**
 * Delete starter template mutation
 */
export const useDeleteStarterTemplateMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteTemplate,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: starterTemplateKeys.all });
        },
    });
};

// ============================================================================
// BLOG QUERY KEYS
// ============================================================================
export const blogKeys = {
    all: ['blogs'] as const,
    lists: () => [...blogKeys.all, 'list'] as const,
    list: (filters: string) => [...blogKeys.lists(), { filters }] as const,
    featured: () => [...blogKeys.all, 'featured'] as const,
    search: (searchTerm: string) => [...blogKeys.all, 'search', searchTerm] as const,
    related: (blogId: string) => [...blogKeys.all, 'related', blogId] as const,
    categories: () => [...blogKeys.all, 'categories'] as const,
    details: () => [...blogKeys.all, 'detail'] as const,
    detail: (id: string) => [...blogKeys.details(), id] as const,
    slug: (slug: string) => [...blogKeys.all, 'slug', slug] as const,
};

// ============================================================================
// BLOG HOOKS
// ============================================================================

/**
 * Get all blogs with optional filters
 */
export const useBlogsQuery = (filters?: {
    category?: string;
    subcategory?: string;
    language?: 'Bengali' | 'English';
    difficulty?: 'Beginner' | 'Intermediate' | 'Advanced';
    published?: boolean;
    featured?: boolean;
    limit?: number;
}) => {
    return useQuery({
        queryKey: blogKeys.list(JSON.stringify(filters || {})),
        queryFn: () => getBlogs(filters),
        staleTime: 5 * 60 * 1000,
    });
};

/**
 * Get featured blogs
 */
export const useFeaturedBlogsQuery = () => {
    return useQuery({
        queryKey: blogKeys.featured(),
        queryFn: getFeaturedBlogPosts,
        staleTime: 10 * 60 * 1000,
    });
};

/**
 * Get blog by ID
 */
export const useBlogQuery = (id: string) => {
    return useQuery({
        queryKey: blogKeys.detail(id),
        queryFn: () => getBlog(id),
        enabled: !!id,
        staleTime: 5 * 60 * 1000,
    });
};

/**
 * Get blog by slug
 */
export const useBlogBySlugQuery = (slug: string) => {
    return useQuery({
        queryKey: blogKeys.slug(slug),
        queryFn: () => getBlogBySlug(slug),
        enabled: !!slug,
        staleTime: 5 * 60 * 1000,
    });
};

/**
 * Search blogs
 */
export const useSearchBlogsQuery = (searchTerm: string, filters?: {
    category?: string;
    language?: 'Bengali' | 'English';
    difficulty?: 'Beginner' | 'Intermediate' | 'Advanced';
}) => {
    return useQuery({
        queryKey: blogKeys.search(searchTerm + JSON.stringify(filters || {})),
        queryFn: () => searchBlogPosts(searchTerm, filters),
        enabled: !!searchTerm && searchTerm.length > 2,
        staleTime: 2 * 60 * 1000,
    });
};

/**
 * Get related blogs
 */
export const useRelatedBlogsQuery = (blogId: string, limit?: number) => {
    return useQuery({
        queryKey: blogKeys.related(blogId),
        queryFn: () => getRelatedBlogPosts(blogId, limit),
        enabled: !!blogId,
        staleTime: 10 * 60 * 1000,
    });
};

/**
 * Get all blog categories
 */
export const useBlogCategoriesQuery = () => {
    return useQuery({
        queryKey: blogKeys.categories(),
        queryFn: getBlogCategories,
        staleTime: 30 * 60 * 1000, // 30 minutes (categories don't change often)
    });
};

/**
 * Create blog mutation
 */
export const useCreateBlogMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createBlogPost,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: blogKeys.all });
        },
    });
};

/**
 * Update blog mutation
 */
export const useUpdateBlogMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<Omit<BlogPost, 'id' | 'createdAt' | 'updatedAt'>> }) =>
            updateBlogPost(id, data),
        onSuccess: (_, { id, data }) => {
            queryClient.invalidateQueries({ queryKey: blogKeys.all });
            queryClient.invalidateQueries({ queryKey: blogKeys.detail(id) });
            if (data.slug) {
                queryClient.invalidateQueries({ queryKey: blogKeys.slug(data.slug) });
            }
        },
    });
};

/**
 * Delete blog mutation
 */
export const useDeleteBlogMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteBlogPost,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: blogKeys.all });
        },
    });
};

/**
 * Increment blog views mutation
 */
export const useIncrementBlogViewsMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: incrementBlogViews,
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: blogKeys.detail(id) });
        },
    });
};

/**
 * Create blog category mutation
 */
export const useCreateBlogCategoryMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createCategory,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: blogKeys.categories() });
        },
    });
};

/**
 * Update blog category mutation
 */
export const useUpdateBlogCategoryMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<Omit<BlogCategory, 'id'>> }) =>
            updateCategory(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: blogKeys.categories() });
        },
    });
};

/**
 * Delete blog category mutation
 */
export const useDeleteBlogCategoryMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteCategory,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: blogKeys.categories() });
        },
    });
}; 